/**
 * Create-ASN-from-delivery-lines modal.
 *
 * Used by the Delivery Lines sub-tab: the user has already multi-selected
 * released delivery lines (`call_off.DeliveryLine`), so the source-selection
 * step is skipped. This is a 2-step flow — Shipment Details → Review &
 * Submit — that reuses the existing {@link ShipmentDetailsStep} and
 * {@link ReviewStep} components. On submit it creates ONE ASN from all
 * selected delivery-line ids via
 * `purchasingAPI.createASN({ delivery_line_ids })`.
 */
import { useState } from 'react';
import { X, Package, Check } from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { purchasingAPI } from '../../../lib/api';
import { ShipmentDetailsStep } from './ShipmentDetailsStep';
import { ReviewStep } from './ReviewStep';
import { ASNFormData, INITIAL_FORM_DATA } from './types';

interface CreateASNFromLinesModalProps {
  isOpen: boolean;
  /** Released delivery-line ids the ASN will be created from. */
  deliveryLineIds: number[];
  onClose: () => void;
  /** Called after a successful create so the parent can reset selection. */
  onCreated: () => void;
}

const STEPS = ['Shipment Details', 'Review & Submit'] as const;

/**
 * Two-step indicator for the lines-based create flow (the 3-step
 * {@link StepIndicator} is wired to the source-selection wizard).
 */
function MiniStepIndicator({ currentStep }: { currentStep: 1 | 2 }) {
  return (
    <div className="flex items-center justify-between mb-2">
      {STEPS.map((label, idx) => {
        const stepNum = (idx + 1) as 1 | 2;
        const isDone = stepNum < currentStep;
        const isCurrent = stepNum === currentStep;
        return (
          <div key={label} className="flex items-center flex-1">
            <div className="flex items-center gap-2">
              <div
                className={`flex items-center justify-center w-7 h-7 rounded-full text-xs font-semibold ${
                  isDone ? 'bg-green-600 text-white' : isCurrent ? 'bg-blue-600 text-white' : 'bg-muted text-muted-foreground'
                }`}
              >
                {isDone ? <Check className="h-4 w-4" /> : stepNum}
              </div>
              <span className={`text-xs font-medium ${isCurrent ? 'text-blue-600' : isDone ? 'text-green-700' : 'text-muted-foreground'}`}>
                {label}
              </span>
            </div>
            {idx < STEPS.length - 1 && <div className={`h-px flex-1 mx-3 ${isDone ? 'bg-green-600' : 'bg-border'}`} />}
          </div>
        );
      })}
    </div>
  );
}

export function CreateASNFromLinesModal({ isOpen, deliveryLineIds, onClose, onCreated }: CreateASNFromLinesModalProps) {
  const [currentStep, setCurrentStep] = useState<1 | 2>(1);
  const [formData, setFormData] = useState<ASNFormData>(INITIAL_FORM_DATA);
  const [submitting, setSubmitting] = useState(false);

  const handleFieldChange = (field: keyof ASNFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const shipmentDetailsValid =
    !!formData.tracking_number &&
    !!formData.expected_delivery_date &&
    !!formData.weight_kg &&
    (formData.courier !== 'other' || !!formData.courier_other);

  const resetAndClose = () => {
    setFormData(INITIAL_FORM_DATA);
    setCurrentStep(1);
    onClose();
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await purchasingAPI.createASN({
        delivery_line_ids: deliveryLineIds,
        courier: formData.courier,
        courier_other: formData.courier_other,
        tracking_number: formData.tracking_number,
        method_of_transport: formData.method_of_transport,
        weight_kg: formData.weight_kg,
        estimated_delivery_date: formData.expected_delivery_date,
        notes: formData.notes,
      });
      resetAndClose();
      onCreated();
    } catch (error) {
      console.error('Failed to create ASN:', error);
      throw error;
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Package className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">Create ASN from {deliveryLineIds.length} delivery {deliveryLineIds.length === 1 ? 'line' : 'lines'}</h3>
          </div>
          <button onClick={resetAndClose} className="p-1 hover:bg-muted rounded-full transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        <CardContent className="p-4 space-y-4">
          <MiniStepIndicator currentStep={currentStep} />

          {currentStep === 1 && (
            <ShipmentDetailsStep formData={formData} onChange={handleFieldChange} />
          )}
          {currentStep === 2 && (
            <ReviewStep sourceType="delivery_lines" formData={{ ...formData, delivery_line_ids: deliveryLineIds }} callOffEnabled={false} />
          )}

          <div className="flex justify-between gap-3 pt-2 border-t">
            <button
              type="button"
              onClick={resetAndClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <div className="flex gap-3">
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={() => setCurrentStep(1)}
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
                >
                  Back
                </button>
              )}
              {currentStep < 2 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(2)}
                  disabled={!shipmentDetailsValid}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting || !shipmentDetailsValid || deliveryLineIds.length === 0}
                  className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                >
                  <Package className="h-4 w-4" />
                  {submitting ? 'Creating...' : 'Create ASN'}
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
